import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Vendors from "./pages/Vendors";
import Customers from "./pages/Customers";
import Procurement from "./pages/Procurement";
import PO from "./pages/PO";
import Logistics from "./pages/Logistics";
import Inventory from "./pages/Inventory";
import Settings from "./pages/Settings";
import Users from "./pages/Users";
import Profile from "./pages/Profile";
import Reports from "./pages/Reports";
import Opportunities from "./pages/Opportunities";
import Tasks from "./pages/Tasks";
import Partners from "./pages/Partners";
import WeeklyStatus from "./pages/WeeklyStatus";
import Financials from "./pages/Financials";
import Support from "./pages/Support";
import TeamsChat from "./pages/TeamsChat";
import Meetings from "./pages/Meetings";
import Marketing from "./pages/Marketing";
import AuditLogs from "./pages/AuditLogs";
import BackupRecovery from "./pages/BackupRecovery";
import ComplianceCenter from "./pages/ComplianceCenter";
import { db } from "./firebase/config";


import { collection, query, where, onSnapshot, doc } from "firebase/firestore";

function AppContent() {
  const { currentUser, logout } = useAuth();
  const [userRoles, setUserRoles] = useState([]);

  // Listen to user document for theme setting and real-time deprovisioning check
  useEffect(() => {
    if (!currentUser || !currentUser.email) {
      document.body.classList.remove("light-mode");
      return;
    }

    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        if (userData.status === "Disabled" || userData.deleted === true) {
          logout("USER_DISABLED");
          return;
        }
        setUserRoles(userData.roles || []);
        if (userData.theme === "light") {
          document.body.classList.add("light-mode");
        } else {
          document.body.classList.remove("light-mode");
        }
      } else {
        document.body.classList.remove("light-mode");
        setUserRoles([]);
      }
    }, (err) => {
      console.error("Error listening to user theme:", err);
    });

    return () => unsubscribe();
  }, [currentUser, logout]);

  // Initialize and persist theme accent color system-wide
  useEffect(() => {
    const savedAccent = localStorage.getItem("global_accent_color");
    if (savedAccent === "purple") {
      document.documentElement.style.setProperty("--primary", "hsl(270, 85%, 60%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(270, 85%, 50%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(270, 85%, 60%, 0.4)");
    } else if (savedAccent === "emerald") {
      document.documentElement.style.setProperty("--primary", "hsl(150, 85%, 45%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(150, 85%, 35%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(150, 85%, 45%, 0.4)");
    } else {
      document.documentElement.style.setProperty("--primary", "hsl(215, 85%, 60%)");
      document.documentElement.style.setProperty("--primary-hover", "hsl(215, 85%, 50%)");
      document.documentElement.style.setProperty("--border-focus", "hsla(215, 85%, 60%, 0.4)");
    }
  }, []);

  // Real-time synchronization for global settings (Default Tax %, etc.) from Firestore
  useEffect(() => {
    const unsubSettings = onSnapshot(doc(db, "settings", "general"), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.defaultTaxRate !== undefined) {
          localStorage.setItem("global_default_tax_rate", data.defaultTaxRate.toString());
        }
      }
    }, (err) => {
      console.warn("Could not sync general settings from Firestore:", err);
    });
    return () => unsubSettings();
  }, []);

  if (!currentUser) {
    return <Login />;
  }

  // Role Access Checks
  const canAccessBD = userRoles.includes("Business Development") || userRoles.includes("Admin");
  const canAccessProcurement = userRoles.some(r => ["Admin", "Manager", "Procurement", "Logistics", "Warehousing", "Sales", "Developer"].includes(r));
  const canAccessCRM = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales"].includes(r));
  const canAccessManagement = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales", "Developer"].includes(r));
  const canAccessSettings = userRoles.some(r => ["Admin", "Manager", "Procurement", "Sales", "Developer"].includes(r));
  const canAccessAdminTools = userRoles.includes("Admin");
  const canAccessDeveloperSupport = userRoles.includes("Developer") || userRoles.includes("Admin");

  return (
    <Router>
      <div className="app-container">
        <Navbar />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/chat" element={<TeamsChat />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/profile" element={<Profile />} />

            {/* Procurement Routes */}
            <Route path="/po" element={canAccessProcurement ? <PO /> : <Navigate to="/" replace />} />
            <Route path="/procurement" element={canAccessProcurement ? <Procurement /> : <Navigate to="/" replace />} />
            <Route path="/logistics" element={canAccessProcurement ? <Logistics /> : <Navigate to="/" replace />} />
            <Route path="/inventory" element={canAccessProcurement ? <Inventory /> : <Navigate to="/" replace />} />

            {/* Business Development Routes */}
            <Route path="/opportunities" element={canAccessBD ? <Opportunities /> : <Navigate to="/" replace />} />
            <Route path="/marketing" element={canAccessBD ? <Marketing /> : <Navigate to="/" replace />} />
            <Route path="/partners" element={canAccessBD ? <Partners /> : <Navigate to="/" replace />} />
            <Route path="/weekly-status" element={canAccessBD ? <WeeklyStatus /> : <Navigate to="/" replace />} />
            <Route path="/meetings" element={canAccessBD ? <Meetings /> : <Navigate to="/" replace />} />

            {/* CRM Routes */}
            <Route path="/customers" element={canAccessCRM ? <Customers /> : <Navigate to="/" replace />} />
            <Route path="/vendors" element={canAccessCRM ? <Vendors /> : <Navigate to="/" replace />} />

            {/* Management Routes */}
            <Route path="/reports" element={canAccessManagement ? <Reports /> : <Navigate to="/" replace />} />
            <Route path="/financials" element={canAccessManagement ? <Financials /> : <Navigate to="/" replace />} />
            <Route path="/support" element={canAccessDeveloperSupport ? <Support /> : <Navigate to="/" replace />} />

            {/* Admin & Settings Routes */}
            <Route path="/settings" element={canAccessSettings ? <Settings /> : <Navigate to="/" replace />} />
            <Route path="/users" element={canAccessAdminTools ? <Users /> : <Navigate to="/" replace />} />
            <Route path="/compliance" element={canAccessAdminTools ? <ComplianceCenter /> : <Navigate to="/" replace />} />
            <Route path="/audit-logs" element={canAccessAdminTools ? <AuditLogs /> : <Navigate to="/" replace />} />
            <Route path="/backup-recovery" element={canAccessAdminTools ? <BackupRecovery /> : <Navigate to="/" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
